/**
 * Kernel tracing for the two-window walkthrough in MANUAL_VERIFICATION.md.
 *
 * On by default outside production. A quote flush lands every
 * `QUOTE_FLUSH_MS`, so this is noisy — silence it per context with
 * `__ATD_DEBUG_KERNEL = false` (window console or SharedWorker inspector).
 * Force it on with `true`. A SharedWorker cannot reach `localStorage` or
 * `window.env`; the override is a global so it needs no protocol plumbing.
 *
 * Socket lifecycle is traced as well as quote traffic, because outside market
 * hours a healthy socket is silent: without the lifecycle events, "never
 * connected" and "connected with nothing to send" look identical.
 */
const runtimeEnv = (): string | undefined => {
  const env = Reflect.get(globalThis, "env");
  if (env === null || typeof env !== "object") {
    return undefined;
  }
  const value = Reflect.get(env, "ENV");
  return typeof value === "string" ? value : undefined;
};

const tracingEnabled = (): boolean => {
  const override = Reflect.get(globalThis, "__ATD_DEBUG_KERNEL");
  if (override === true) {
    return true;
  }
  if (override === false) {
    return false;
  }
  const env = runtimeEnv();
  if (env !== undefined) {
    return env !== "PROD";
  }
  // SharedWorker (and tests with an empty `window.env`) have no runtime ENV.
  // Vite's PROD flag is true for staging and production builds alike, so the
  // worker only auto-enables in a Vite dev build.
  return import.meta.env.DEV && import.meta.env.MODE !== "test";
};

export const debugKernel = (
  event: string,
  details: Record<string, unknown>,
): void => {
  if (!tracingEnabled()) {
    return;
  }
  // biome-ignore lint/suspicious/noConsole: scoped diagnostics are intentional in development
  console.debug(`[atd kernel] ${event}`, details);
};
